// src/components/Messages.js
import { useEffect, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, FormControl, InputLabel,
  MenuItem, Paper, Select, Stack, TextField, Typography,
} from "@mui/material";
import * as groupRequestsService from "../services/groupRequestsService";
import { useUsersQuery } from "../hooks/useUsers";
import {
  useRequestsQuery, useNotifySettingsQuery,
  useSubmitRequest, useApproveRequest, useRejectRequest,
  useReceiveFeedback, useAddFeedbackToTodo, useAssignVacancyLeader, useSetNotifyTarget,
  useSendAdminMessage,
} from "../hooks/useGroupRequests";
import { useGroupsQuery, useMembershipsQuery } from "../hooks/useGroups";
import GoHomeButton from "./GoHomeButton";

const REQUEST_TYPE_LABELS = {
  join: "Join a group",
  leave: "Leave a group",
  create: "Request a new group",
  admin_add: "Admin: added to group",
  admin_remove: "Admin: removed from group",
  leader_change: "Change group leader",
  leader_vacancy: "Leader vacancy",
  bulk_invite: "Invite members",
  feedback: "Feedback",
  admin_message: "Message from Admin",
};

const ALL_USERS_OPTION = { type: "all", id: "__all__", label: "All Users" };

function systemMessage(request) {
  const name = request.groups?.name || request.new_group_name || "the group";
  switch (request.request_type) {
    case "join":
      return request.status === "approved"
        ? `Your request to join "${name}" was approved. You've been added.`
        : `Your request to join "${name}" was rejected.`;
    case "leave":
      return request.status === "approved"
        ? `Your request to leave "${name}" was approved. You've been removed.`
        : `Your request to leave "${name}" was rejected.`;
    case "create":
      return request.status === "approved"
        ? `"${name}" has been created and you've been added.`
        : `Your request to create "${name}" was rejected.`;
    case "admin_add":
      return `You've been added to "${name}" by the administrator.`;
    case "admin_remove":
      return `You've been removed from "${name}" by the administrator.`;
    case "leader_change":
      return request.status === "approved"
        ? `Your request to change the Leader of "${name}" was approved.`
        : `Your request to change the Leader of "${name}" was rejected.`;
    case "bulk_invite": {
      if (request.status !== "approved") {
        return `Your invite request for "${name}" was rejected.`;
      }
      const results = Array.isArray(request.invite_results) ? request.invite_results : [];
      const lines = results.map(
        (r) => `${r.email}: ${r.status === "invited" ? "Invited" : `Failed (${r.error || "error"})`}`
      );
      return [`Your invite request for "${name}" was approved.`, ...lines].join("\n");
    }
    case "feedback":
      return request.admin_response || "Thanks for your feedback! It's been received.";
    case "admin_message":
      return request.admin_response;
    default:
      return "";
  }
}

const statusColor = (req) => {
  if (req.request_type === "admin_remove") return "error";
  return { pending: "warning", approved: "success", rejected: "error", received: "info" }[req.status] || "default";
};

export default function Messages({ userId, isAdmin, onNavigateHome }) {
  const { data: requests = [] } = useRequestsQuery({ userId, isAdmin });
  const { data: allGroups = [] } = useGroupsQuery();
  const { data: allMemberships = [] } = useMembershipsQuery();
  const { data: notifySettings } = useNotifySettingsQuery({ isAdmin });

  const submitRequestMutation = useSubmitRequest();
  const submitFeedbackMutation = useSubmitRequest();
  const approveMutation = useApproveRequest();
  const rejectMutation = useRejectRequest();
  const receiveFeedbackMutation = useReceiveFeedback();
  const addFeedbackToTodoMutation = useAddFeedbackToTodo();
  const assignVacancyMutation = useAssignVacancyLeader();
  const setNotifyTargetMutation = useSetNotifyTarget();
  const sendMessageMutation = useSendAdminMessage();

  const { data: usersAll = [] } = useUsersQuery();
  // `displayNameFor`/`membersOfGroup` below are also called from the request-history
  // section, which renders for non-admins too (their own requests) — not just from the
  // isAdmin-gated pending-requests section. RLS already restricts what a non-admin's
  // `usersAll` read returns, but gate it here anyway so a non-admin never sees the
  // full user list, even indirectly via a display-name lookup.
  const visibleUsersAll = isAdmin ? usersAll : [];

  useEffect(() => {
    if (isAdmin || !userId) return;
    groupRequestsService.markRequestsRead({ userId }).catch(() => {});
  }, [userId, isAdmin]);

  const [requestType, setRequestType] = useState("create");
  const [targetGroupId, setTargetGroupId] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [confirm, setConfirm] = useState(null); // {requestId, action, request}
  const [vacancyTarget, setVacancyTarget] = useState({}); // { [requestId]: userId }
  const [feedbackText, setFeedbackText] = useState("");
  const [replyText, setReplyText] = useState("");
  const [todoItemText, setTodoItemText] = useState("");
  const [actionError, setActionError] = useState(null);
  const [audienceSelection, setAudienceSelection] = useState([]);
  const [messageText, setMessageText] = useState("");

  const userGroupIds = allMemberships.filter((m) => m.user_id === userId).map((m) => m.group_id);

  const submitRequest = async () => {
    if (requestType === "create" && !newGroupName.trim()) return;
    if (requestType !== "create" && !targetGroupId) return;
    if (requestType === "leave" && userGroupIds.length <= 1) return;
    try {
      await submitRequestMutation.mutateAsync({
        fromUserId: userId,
        requestType,
        groupId: requestType !== "create" ? targetGroupId : null,
        newGroupName: requestType === "create" ? newGroupName.trim() : null,
      });
      setTargetGroupId("");
      setNewGroupName("");
    } catch {
      setActionError("Failed to submit request. Please try again.");
    }
  };

  const submitFeedback = async () => {
    if (!feedbackText.trim()) return;
    try {
      await submitFeedbackMutation.mutateAsync({ fromUserId: userId, requestType: "feedback", feedbackMessage: feedbackText.trim() });
      setFeedbackText("");
    } catch {
      setActionError("Failed to submit feedback. Please try again.");
    }
  };

  const handleNotifyTargetChange = (value) => {
    setNotifyTargetMutation.mutate({ notifyAdminUserId: value || null });
  };

  const executeApprove = async () => {
    if (!confirm) return;
    const { requestId, request } = confirm;
    const displayName = displayNameFor(request.from_user_id);
    try {
      await approveMutation.mutateAsync({
        requestId,
        requestType: request.request_type,
        fromUserId: request.from_user_id,
        groupId: request.group_id,
        targetUserId: request.target_user_id,
        newGroupName: request.new_group_name,
        invitedEmails: request.invited_emails,
        resolvedBy: userId,
        displayName,
      });
      setConfirm(null);
    } catch {
      setActionError("Failed to approve request. Please try again.");
    }
  };

  const executeReject = async () => {
    if (!confirm) return;
    try {
      await rejectMutation.mutateAsync({
        requestId: confirm.requestId,
        resolvedBy: userId,
        requestType: confirm.request?.request_type,
        fromUserId: confirm.request?.from_user_id,
      });
      setConfirm(null);
    } catch {
      setActionError("Failed to reject request. Please try again.");
    }
  };

  const executeReceive = async () => {
    if (!confirm) return;
    try {
      await receiveFeedbackMutation.mutateAsync({ requestId: confirm.requestId, resolvedBy: userId, replyText });
      setConfirm(null);
      setReplyText("");
    } catch {
      setActionError("Failed to submit response. Please try again.");
    }
  };

  const executeAddToTodo = async () => {
    if (!confirm || !todoItemText.trim()) return;
    try {
      await addFeedbackToTodoMutation.mutateAsync({ requestId: confirm.requestId, todoItemText, addedBy: userId });
      setConfirm(null);
      setTodoItemText("");
    } catch {
      setActionError("Failed to add to the to-do list. Please try again.");
    }
  };

  const renderAddToTodoButton = (req) =>
    req.todo_item_text ? (
      <Chip label="✓ Added to ToDo" size="small" color="success" variant="outlined" />
    ) : (
      <Button size="small" variant="outlined" color="secondary"
        onClick={() => {
          setTodoItemText(req.feedback_message || "");
          setConfirm({ requestId: req.id, action: "addToTodo", request: req });
        }}>
        Add to ToDo
      </Button>
    );

  const membersOfGroup = (groupId) =>
    allMemberships
      .filter((m) => m.group_id === groupId)
      .map((m) => ({ userId: m.user_id, displayName: visibleUsersAll.find((u) => u.user_id === m.user_id)?.display_name || m.user_id }));

  const displayNameFor = (targetUserId) =>
    visibleUsersAll.find((u) => u.user_id === targetUserId)?.display_name || targetUserId;

  const audienceOptions = isAdmin
    ? [
        ALL_USERS_OPTION,
        ...allGroups.map((g) => ({ type: "group", id: g.id, label: g.name, section: "Groups" })),
        ...visibleUsersAll
          .map((u) => ({
            type: "user",
            id: u.user_id,
            label: u.display_name || u.user_id,
            section: allGroups.find((g) => g.id === u.primary_group_id)?.name || "No Group",
          }))
          .sort((a, b) => a.section.localeCompare(b.section)),
      ]
    : [];

  const handleAudienceChange = (event, newValue) => {
    const hasAll = newValue.some((opt) => opt.type === "all");
    const hadAllBefore = audienceSelection.some((opt) => opt.type === "all");
    if (hasAll && !hadAllBefore) {
      setAudienceSelection([ALL_USERS_OPTION]);
    } else if (hasAll && hadAllBefore && newValue.length > 1) {
      setAudienceSelection(newValue.filter((opt) => opt.type !== "all"));
    } else {
      setAudienceSelection(newValue);
    }
  };

  const resolveSelectedRecipientIds = () => {
    const allUsers = audienceSelection.some((opt) => opt.type === "all");
    const groupIds = audienceSelection.filter((opt) => opt.type === "group").map((opt) => opt.id);
    const userIds = audienceSelection.filter((opt) => opt.type === "user").map((opt) => opt.id);
    return groupRequestsService.resolveRecipientIds({
      selection: { allUsers, groupIds, userIds, allUserIds: visibleUsersAll.map((u) => u.user_id) },
      allMemberships,
    });
  };

  const recipientPreviewCount = isAdmin ? resolveSelectedRecipientIds().length : 0;

  const openSendMessageConfirm = () => {
    const recipientIds = resolveSelectedRecipientIds();
    const trimmedText = messageText.trim();
    if (recipientIds.length === 0 || !trimmedText) return;
    setConfirm({ action: "sendMessage", recipientIds, messageText: trimmedText, recipientCount: recipientIds.length });
  };

  const executeSendMessage = async () => {
    if (!confirm) return;
    try {
      await sendMessageMutation.mutateAsync({ recipientIds: confirm.recipientIds, messageText: confirm.messageText, sentBy: userId });
      setConfirm(null);
      setAudienceSelection([]);
      setMessageText("");
    } catch {
      setActionError("Failed to send message. Please try again.");
    }
  };

  const assignVacancyLeader = async (request) => {
    const targetUserId = vacancyTarget[request.id];
    if (!targetUserId) return;
    try {
      await assignVacancyMutation.mutateAsync({ requestId: request.id, resolvedBy: userId, groupId: request.group_id, targetUserId });
      setVacancyTarget((prev) => {
        const next = { ...prev };
        delete next[request.id];
        return next;
      });
    } catch {
      setActionError("Failed to assign leader. Please try again.");
    }
  };

  const pendingRequests = requests.filter((r) => r.status === "pending");
  const resolvedRequests = requests.filter((r) => r.status !== "pending" && r.request_type !== "admin_message");
  const leavableGroups = allGroups.filter((g) => userGroupIds.includes(g.id));

  const sentMessageBatches = isAdmin
    ? Object.values(
        requests
          .filter((r) => r.request_type === "admin_message")
          .reduce((batches, r) => {
            (batches[r.batch_id] ??= []).push(r);
            return batches;
          }, {})
      ).sort((a, b) => new Date(b[0].created_at) - new Date(a[0].created_at))
    : [];

  return (
    <Paper className="page-card" sx={{ p: 3, mb: 4 }}>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <GoHomeButton onNavigateHome={onNavigateHome} />
      </Stack>
      {actionError && (
        <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}
      {/* Admin: email notification target */}
      {isAdmin && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="notify-admin-label" shrink>Notify by email</InputLabel>
            <Select
              labelId="notify-admin-label"
              label="Notify by email"
              displayEmpty
              value={notifySettings?.notify_admin_user_id || ""}
              onChange={(e) => handleNotifyTargetChange(e.target.value)}
            >
              <MenuItem value="">All Admins</MenuItem>
              {visibleUsersAll.filter((u) => u.role === "admin").map((u) => (
                <MenuItem key={u.user_id} value={u.user_id}>{u.display_name || u.user_id}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}
      {/* Admin: send a message to selected recipients */}
      {isAdmin && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Send a Message</Typography>
          <Stack spacing={2}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              size="small"
              options={audienceOptions}
              groupBy={(opt) => (opt.type === "all" ? "All" : opt.type === "group" ? "Groups" : opt.section)}
              getOptionLabel={(opt) => opt.label}
              isOptionEqualToValue={(opt, val) => opt.type === val.type && opt.id === val.id}
              value={audienceSelection}
              onChange={handleAudienceChange}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                    {option.label}
                  </li>
                );
              }}
              renderInput={(params) => <TextField {...params} label="Send to" placeholder="Select recipients" />}
            />
            <TextField
              size="small"
              label="Message"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {recipientPreviewCount} recipient{recipientPreviewCount !== 1 ? "s" : ""}
              </Typography>
              <Button
                variant="contained"
                onClick={openSendMessageConfirm}
                disabled={audienceSelection.length === 0 || !messageText.trim()}
              >
                Send Message
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* User: new request form */}
      {!isAdmin && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>New Request</Typography>
          <Stack spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Request type</InputLabel>
              <Select
                label="Request type"
                value={requestType}
                onChange={(e) => { setRequestType(e.target.value); setTargetGroupId(""); }}
              >
                <MenuItem value="leave">Be removed from a group</MenuItem>
                <MenuItem value="create">Request a new group be created</MenuItem>
              </Select>
            </FormControl>

            {requestType === "leave" && (
              <>
                <FormControl size="small" fullWidth>
                  <InputLabel>Group to leave</InputLabel>
                  <Select label="Group to leave" value={targetGroupId} onChange={(e) => setTargetGroupId(e.target.value)}>
                    {leavableGroups.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
                  </Select>
                </FormControl>
                {userGroupIds.length <= 1 && (
                  <Typography variant="caption" color="error">
                    You can't leave your only group. Join another group first using a join code in Settings → My Groups.
                  </Typography>
                )}
              </>
            )}

            {requestType === "create" && (
              <TextField
                size="small"
                label="New group name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                fullWidth
              />
            )}

            <Button
              variant="contained"
              onClick={submitRequest}
              disabled={
                submitRequestMutation.isPending ||
                (requestType === "create" && !newGroupName.trim()) ||
                (requestType !== "create" && !targetGroupId) ||
                (requestType === "leave" && userGroupIds.length <= 1)
              }
            >
              {submitRequestMutation.isPending ? "Sending..." : "Send Request"}
            </Button>
          </Stack>
        </Paper>
      )}

      {/* User: send feedback */}
      {!isAdmin && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Send Feedback</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Have feedback — good or bad? Found a bug, or have an idea for a new feature? Let us know below.
          </Typography>
          <Stack spacing={2}>
            <TextField
              size="small"
              label="Your feedback"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={submitFeedback}
                disabled={submitFeedbackMutation.isPending || !feedbackText.trim()}
              >
                {submitFeedbackMutation.isPending ? "Sending..." : "Send"}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setFeedbackText("")}
                disabled={submitFeedbackMutation.isPending || !feedbackText}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Admin: pending requests */}
      {isAdmin && pendingRequests.length > 0 && (
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary">
            PENDING — {pendingRequests.length} request{pendingRequests.length !== 1 ? "s" : ""}
          </Typography>
          {pendingRequests.map((req) => (
            <Paper key={req.id} variant="outlined" sx={{ p: 2, borderLeft: "4px solid", borderColor: "warning.main" }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {req.request_type === "leader_vacancy" ? "System notice" : req.from_user_id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {REQUEST_TYPE_LABELS[req.request_type]}
                    {req.groups?.name ? `: "${req.groups.name}"` : ""}
                    {req.new_group_name ? `: "${req.new_group_name}"` : ""}
                    {req.request_type === "leader_change" && req.target_user_id ? ` → ${displayNameFor(req.target_user_id)}` : ""}
                    {req.request_type === "leader_vacancy" && " — previous Leader's account was deleted"}
                    {req.request_type === "bulk_invite" && Array.isArray(req.invited_emails)
                      ? ` — ${req.invited_emails.join(", ")}`
                      : ""}
                  </Typography>
                  {req.request_type === "feedback" && (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-line", mt: 0.5 }}>
                      {req.feedback_message}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block">{new Date(req.created_at).toLocaleDateString()}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{new Date(req.created_at).toLocaleTimeString()}</Typography>
                </Box>
                {req.request_type === "leader_vacancy" ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>New Leader</InputLabel>
                      <Select
                        label="New Leader"
                        value={vacancyTarget[req.id] ?? ""}
                        onChange={(e) => setVacancyTarget((prev) => ({ ...prev, [req.id]: e.target.value }))}
                      >
                        {membersOfGroup(req.group_id).map((m) => (
                          <MenuItem key={m.userId} value={m.userId}>{m.displayName}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button size="small" variant="outlined" color="success"
                      disabled={!vacancyTarget[req.id]}
                      onClick={() => assignVacancyLeader(req)}>
                      Assign Leader
                    </Button>
                    <Button size="small" variant="outlined" color="error"
                      onClick={() => setConfirm({ requestId: req.id, action: "reject", request: req })}>
                      Dismiss
                    </Button>
                  </Stack>
                ) : req.request_type === "feedback" ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    {renderAddToTodoButton(req)}
                    <Button size="small" variant="outlined" color="info"
                      onClick={() => setConfirm({ requestId: req.id, action: "receive", request: req })}>
                      Received
                    </Button>
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" color="success"
                      onClick={() => setConfirm({ requestId: req.id, action: "approve", request: req })}>
                      Approve
                    </Button>
                    <Button size="small" variant="outlined" color="error"
                      onClick={() => setConfirm({ requestId: req.id, action: "reject", request: req })}>
                      Reject
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Admin: sent messages */}
      {isAdmin && sentMessageBatches.length > 0 && (
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary">
            SENT MESSAGES — {sentMessageBatches.length} sent
          </Typography>
          {sentMessageBatches.map((batch) => {
            const first = batch[0];
            const readCount = batch.filter((r) => r.read_by_requester).length;
            return (
              <Paper key={first.batch_id} variant="outlined" sx={{ p: 2 }}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>{first.admin_response}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Sent to {batch.length} recipient{batch.length !== 1 ? "s" : ""} by {displayNameFor(first.resolved_by)} — {readCount} of {batch.length} read
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {new Date(first.created_at).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {new Date(first.created_at).toLocaleTimeString()}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Request history */}
      <Stack spacing={1}>
        <Typography variant="subtitle2" color="text.secondary">
          {isAdmin ? "RESOLVED" : "MY REQUESTS"}
        </Typography>
        {requests.length === 0 && (
          <Typography variant="body2" color="text.secondary">No requests yet.</Typography>
        )}
        {(isAdmin ? resolvedRequests : requests).map((req) => (
          <Paper
            key={req.id}
            variant="outlined"
            sx={{
              p: 1.5,
              borderLeft: "4px solid",
              borderColor: `${statusColor(req)}.main`,
              opacity: isAdmin && req.status !== "pending" ? 0.7 : 1,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {isAdmin && (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {req.request_type === "leader_vacancy" ? "System notice" : req.from_user_id}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  {REQUEST_TYPE_LABELS[req.request_type]}
                  {req.groups?.name ? `: "${req.groups.name}"` : ""}
                  {req.new_group_name ? `: "${req.new_group_name}"` : ""}
                  {req.request_type === "leader_change" && req.target_user_id ? ` → ${displayNameFor(req.target_user_id)}` : ""}
                  {req.request_type === "bulk_invite" && Array.isArray(req.invited_emails)
                    ? ` — ${req.invited_emails.join(", ")}`
                    : ""}
                </Typography>
                {req.request_type === "feedback" && (
                  <Typography variant="body2" sx={{ whiteSpace: "pre-line", mt: 0.5 }}>
                    {req.feedback_message}
                  </Typography>
                )}
                {req.status !== "pending" && !isAdmin && (
                  <Typography variant="caption" color={`${statusColor(req)}.main`} sx={{ whiteSpace: "pre-line" }}>
                    {systemMessage(req)}
                  </Typography>
                )}
              </Box>
              <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                <Chip label={req.status} color={statusColor(req)} size="small" />
                <Typography variant="caption" color="text.secondary" display="block">
                  {new Date(req.created_at).toLocaleDateString()}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {new Date(req.created_at).toLocaleTimeString()}
                </Typography>
                {isAdmin && req.request_type === "feedback" && (
                  <Box sx={{ mt: 1 }}>{renderAddToTodoButton(req)}</Box>
                )}
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>

      {/* Approve/Reject/Receive confirmation dialog */}
      <Dialog open={!!confirm} onClose={() => { setConfirm(null); setReplyText(""); setTodoItemText(""); }}>
        <DialogTitle>
          {confirm?.action === "sendMessage" ? "Send Message"
            : confirm?.action === "receive" ? "Mark Feedback Received"
            : confirm?.action === "addToTodo" ? "Add to ToDo List"
            : confirm?.action === "approve" ? "Approve Request" : "Reject Request"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: "pre-line" }}>
            {confirm?.action === "sendMessage"
              ? `Send this message to ${confirm?.recipientCount} recipient${confirm?.recipientCount !== 1 ? "s" : ""}? This cannot be undone.\n\n"${confirm?.messageText || ""}"`
              : confirm?.action === "receive"
              ? `Mark this feedback from ${confirm?.request?.from_user_id} as received? "${confirm?.request?.feedback_message || ""}"`
              : confirm?.action === "addToTodo"
              ? `Add ${confirm?.request?.from_user_id}'s feedback as a new item under "Customer Feedback" on the project to-do list?`
              : confirm?.request?.request_type === "leader_vacancy"
              ? `Dismiss this Leader-vacancy notice for "${confirm?.request?.groups?.name || "the group"}"? The group will remain without a Leader until one is assigned.`
              : confirm?.request?.request_type === "bulk_invite"
              ? (confirm?.action === "approve"
                  ? `Approve this invite request for "${confirm?.request?.groups?.name || "the group"}"? Invite emails will be sent to: ${(confirm?.request?.invited_emails || []).join(", ")}.`
                  : `Reject this invite request? No emails will be sent.`)
              : confirm?.action === "approve"
              ? `Approve ${confirm?.request?.from_user_id}'s request to ${REQUEST_TYPE_LABELS[confirm?.request?.request_type]?.toLowerCase()}
                 ${confirm?.request?.groups?.name ? `"${confirm.request.groups.name}"` : confirm?.request?.new_group_name ? `"${confirm.request.new_group_name}"` : ""}?
                 This will automatically update their group memberships.`
              : `Reject ${confirm?.request?.from_user_id}'s request? No changes will be made to their group memberships.`
            }
          </DialogContentText>
          {confirm?.action === "receive" && (
            <TextField
              size="small"
              label="Reply (optional)"
              helperText="Leave blank to send the standard 'received' notice, or write a custom reply."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              multiline
              minRows={2}
              fullWidth
              sx={{ mt: 2 }}
            />
          )}
          {confirm?.action === "addToTodo" && (
            <TextField
              size="small"
              label="To-do item text"
              helperText="Edit if you'd like to shape this into a clean backlog item before it's added."
              value={todoItemText}
              onChange={(e) => setTodoItemText(e.target.value)}
              multiline
              minRows={2}
              fullWidth
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirm(null); setReplyText(""); setTodoItemText(""); }}>Cancel</Button>
          <Button
            color={
              confirm?.action === "sendMessage" ? "primary"
              : confirm?.action === "receive" ? "info"
              : confirm?.action === "addToTodo" ? "secondary"
              : confirm?.action === "approve" ? "success" : "error"
            }
            variant="contained"
            disabled={confirm?.action === "addToTodo" && !todoItemText.trim()}
            onClick={
              confirm?.action === "sendMessage" ? executeSendMessage
              : confirm?.action === "receive" ? executeReceive
              : confirm?.action === "addToTodo" ? executeAddToTodo
              : confirm?.action === "approve" ? executeApprove
              : executeReject
            }
          >
            {confirm?.action === "sendMessage" ? "Confirm Send"
              : confirm?.action === "receive" ? "Confirm Received"
              : confirm?.action === "addToTodo" ? "Add to ToDo"
              : confirm?.action === "approve" ? "Confirm Approve" : "Confirm Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
